// @ts-nocheck
import React, { FC, useState, useRef } from 'react';
import { whichAuth as getAuth } from '../../../getAuth';
import { userState, invoiceToApproveState} from '../../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { format } from "date-fns";
import CurrencyFormat from 'react-currency-format';
import { REST_API_URL } from '../../../constants/global';
import { handleErrors } from '../../../utils/handleErrors';
import {
  IonModal,
  IonIcon,
  IonButton,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol,
  IonImg,
  IonHeader,
  IonText,
  IonTextarea,
  IonContent,
  IonPopover,
  IonAlert,
  useIonAlert
} from '@ionic/react';
import { informationCircleOutline } from 'ionicons/icons';
import cn from "classnames";
import { AlertModal, CreditCardForm } from "../../../components";
import { BillsStatus } from "../Bills.model";
import ClockIcon from '../../../assets/clock.svg'
import ArrowLeftBackIcon from '../../../assets/arrow-left-back.svg'
import './InvoiceApproval.css'

const InvoiceApproval: FC = ({markHistorical}) => {
  const modal = useRef<HTMLIonModalElement>(null)
  const modalQuery = useRef<HTMLIonModalElement>(null);
  const modalApprove = useRef<HTMLIonModalElement>(null);
  const modalCreditCard = useRef<HTMLIonModalElement>(null);

  const user = useRecoilValue(userState);
  const setUser = useSetRecoilState(userState);
  const invoiceToApprove = useRecoilValue(invoiceToApproveState);
  const setInvoiceToApprove = useSetRecoilState(invoiceToApproveState);

  const [approvalInProgress, setApprovalInProgress] = useState<boolean>(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [downloadingInvoice, setDownloadingInvoice] = useState<boolean>(false);
  const [submittingQuery, setSubmittingQuery] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalMessage, setModalMessage] = useState<string>('');
  const [showDoNotPayAlert, setShowDoNotPayAlert] = useState<boolean>(false);
  
  const [presentAlert] = useIonAlert();

  const invoiceApproved = invoiceToApprove?.status !== 'USER_APPROVAL';

  const getInvoiceUrl = () => getAuth().currentUser.getIdToken().then(token =>
    fetch(user._links.invoices.href + '/'+ invoiceToApprove?.id, {headers: {'Authorization': 'Bearer ' + token}})
      .then(handleErrors)
      .catch(() => {
        setIsOpenModal(true)
        setModalMessage('Failed to show bill PDF.')
      }));
      
  const openInvoice = () => {
    setDownloadingInvoice(true);
    getInvoiceUrl()
      .then(url => window.open(url, '_blank'))
      .finally(() => setDownloadingInvoice(false));
  }

  const approveInvoice = async () => {
    if(!user?.hasPaymentMethod) {
      modalApprove.current?.present()
    } else {
      setApprovalInProgress(true);
      const token = await getAuth().currentUser.getIdToken()
      try {
        const approvalResponse = await fetch(REST_API_URL + '/invoices/' + invoiceToApprove.id + '/approve', {
          headers: {'Authorization': 'Bearer ' + token}
        })
        if (approvalResponse.status !== 200) {
          throw 'Failed to approve bill.';
        }
        const paymentInfo = await approvalResponse.json()
        setUser({
          ...user,
          paymentInfo,
          updateInvoice: true
        });
        setInvoiceToApprove({...invoiceToApprove, status:'PENDING_PAYMENT'})
        setIsOpenModal(true);
        setModalTitle('Bill approved');
        setModalMessage(<div>
          <p>
            Your bill has been approved and will be paid by&nbsp;
            {format(new Date(invoiceToApprove.dueDate), 'dd MMM yyyy')}.
          </p>
          {user?.paymentOption === 'SMOOV' &&
            <p>
              Your new payment amount is
              ${(paymentInfo.nextPaymentAmount/100).toFixed(2)}&nbsp;
              and will be processed on&nbsp;
              {format(new Date(paymentInfo.nextPaymentDate), 'd MMM')}.
            </p>
          }
          <p>If you have any questions, please contact us.</p>
          </div>);
      } catch (error) {
        setIsOpenModal(true);
        setModalMessage('Failed to approve bill.');
      } finally {
        setApprovalInProgress(false)
      }
    }
  }
  
  const historical = () => {
    markHistorical(invoiceToApprove?.id);
    closeModal();
  }
  
  const redirectedCreditCard = () => {
    modalApprove.current?.dismiss()
    modalCreditCard.current?.present()
  }
  
  const submitQuery = async () => {
    setSubmittingQuery(true);
    const token = await getAuth().currentUser.getIdToken()
    try {
      const { status } = await fetch(REST_API_URL + '/invoices/' + invoiceToApprove.id + '/query', {
        method: 'POST',
        body: JSON.stringify({query}),
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      })
      if (status === 200) {
        presentAlert({
          header:"Query submitted",
          message:"Your query has been received. We will be in touch soon.",
          buttons: ['OK'],
        });
      } else {
        throw new Error('Failed to send query.')
      }
    } catch (error) {
      setIsOpenModal(true)
      setModalMessage(error)
    } finally {
      setSubmittingQuery(false);
      await modalQuery.current?.dismiss()
    }
  }

  const closeModal = async () => {
    await modal.current?.dismiss();
    setInvoiceToApprove(null);
    setInvoiceUrl(null);
    if (user?.billsReceivedList?.length) {
      setUser({ ...user, billsReceivedList: user?.billsReceivedList.filter((item) => item.id !== invoiceToApprove?.id)})
    }
  }
  
  const showInvoice = () => !!invoiceUrl && 
    <div className="invoice-iframe-div">
      <iframe src={invoiceUrl} frameBorder='0' className="invoice-iframe" title="bill" />
    </div>
  
  React.useEffect(() => !!invoiceToApprove && getInvoiceUrl().then(url => setInvoiceUrl(url)), [invoiceToApprove]);

  return (
    !!invoiceToApprove &&
      <IonModal
        isOpen={!!invoiceToApprove}
        trigger={invoiceToApprove?.id}
        className="big-modal"
        onDidDismiss={() => closeModal()}
        ref={modal}
      >
        <IonHeader className="invoice-header">
          <IonIcon icon={ArrowLeftBackIcon} className="invoice-header-icon" onClick={closeModal} />
          <IonText className="invoice-header-text">Billing information</IonText>
        </IonHeader>
        <IonGrid className="invoice-container">
          <IonRow className="invoice-container-row">
            <IonCol className="invoice-container-col left">
              <IonImg
                src={invoiceToApprove?.provider?.logo.replace(/upload/,'upload/w_200,h_200,c_fit').replace(/.svg$/, '.png')}
                alt="provider_logo"
                className="invoice-icon-header"
              />
              <IonCol size="12" className="invoice-container-col-card">
                <div className="invoice-title">
                  {invoiceToApprove?.provider?.service}
                </div>
                { user?.paymentOption !== 'MANUAL' &&
                  <IonText className="invoice-subtitle">
                    {BillsStatus[invoiceToApprove?.status]}
                  </IonText>
                }
                <CurrencyFormat
                  value={invoiceToApprove?.total}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix='$'
                  displayType='text'
                  className="invoice-price"
                />
              </IonCol>
              <IonCol size="12" className="invoice-container-col-card">
                <IonIcon src={ClockIcon} className="invoice-container-col-card-icon-clock" />
                <IonText className="invoice-container-col-card-text">
                  Due date: {format(new Date(invoiceToApprove?.dueDate), 'dd MMM yyy')}
                </IonText>
                <IonText className="invoice-container-col-card-text">
                  Billing period: {format(new Date(invoiceToApprove?.startDate), 'd MMM yy')} - {format(new Date(invoiceToApprove.endDate), 'd MMM yy')}
                </IonText>
              </IonCol>
              <IonCol size="12" className="invoice-container-col-card buttons no-border">
                { user?.paymentOption !== 'MANUAL' &&
                  <>
                    <IonButton
                      className={cn('invoice-button')}
                      onClick={approveInvoice}
                      disabled={invoiceApproved} >
                      {approvalInProgress && <IonSpinner name="dots" />}
                      {invoiceApproved ? 'Approved' : 'Approve'}
                    </IonButton>
                    <IonButton id="invoiceModal" className="invoice-button cancel">
                      Query
                    </IonButton>
                    {!invoiceApproved && (
                      <>
                        <IonButton onClick={() => closeModal()} className="invoice-button cancel">
                          Approve later
                        </IonButton>
                        <IonButton 
                          className="invoice-button cancel"
                          onClick={() => setShowDoNotPayAlert(true)} >
                          Do not pay
                        </IonButton>
                      </>
                    )}
                  </>
                }
                <IonButton onClick={openInvoice} className="invoice-button invoice-download-button">
                  {downloadingInvoice && <IonSpinner name="dots" />}
                  Download
                </IonButton>
                
                <IonModal ref={modalApprove} trigger="invoiceApprove" className="invoice-approve-modal">
                  <div className="invoice-approve-modal-content">
                    <IonText className="invoice-container-col-card-text">
                      Please add your payment details
                    </IonText>
                    <div className="modal-sub-text">
                      Before you can approve your first bill, please add your payment details so we can process your payments.
                      <IonIcon id="paymentInfo" icon={informationCircleOutline} />
                      <IonPopover trigger="paymentInfo" triggerAction="hover">
                        <IonContent class="ion-padding">
                          <p>
                            We will use your credit card to process your payments. 
                            { user?.paymentOption === 'SMOOV' && <span>We will also place a hold on your card for the difference between the bills paid by us and your repayments.</span> }
                          </p>
                          { user?.paymentOption === 'SMOOV' && 
                            <p>
                              Your first repayment date is {format(new Date(user.paymentInfo?.nextPaymentDate), 'dd MMM')}. 
                              When your payments are due we will debit your smooth payment amount from your card and update the amount on hold accordingly.
                            </p>
                          }
                        </IonContent>
                      </IonPopover>
                    </div>
                    <IonCol size="12" className="invoice-container-col-card buttons no-border no-padding margin">
                      <IonButton className="invoice-button" onClick={redirectedCreditCard}>Ok</IonButton>
                      <IonButton className="invoice-button cancel" onClick={() => modalApprove.current?.dismiss()}>
                        Cancel
                      </IonButton>
                    </IonCol>
                  </div>
                </IonModal>
                
                <IonModal ref={modalCreditCard} trigger="creditCard" className="credit-card-modal">
                  <div className="credit-card-modal-content" >
                    <CreditCardForm onClose={() => modalCreditCard.current?.dismiss()} />
                  </div>
                </IonModal>
                
                <IonModal ref={modalQuery} trigger="invoiceModal" className="invoice-query-modal">
                  <IonContent className="invoice-query-modal-content">
                    <IonCol size="12">
                      <IonTextarea
                        rows={5}
                        placeholder="Please submit your query and our team will get in contact."
                        className="invoice-query-modal-input"
                        inputmode='text'
                        onIonChange={event => setQuery(event.detail.value)} />
                    </IonCol>
                    <IonCol size="12" className="invoice-container-col-card buttons no-border no-padding query">
                      <IonButton className="invoice-button" onClick={submitQuery}>{submittingQuery && <IonSpinner name="dots" />} Submit</IonButton>
                      <IonButton className="invoice-button cancel" onClick={() => modalQuery.current?.dismiss()}>Cancel</IonButton>
                    </IonCol>
                  </IonContent>
                </IonModal>
              </IonCol>
            </IonCol>
            <IonCol className="invoice-iframe-col invoice-container-col right">
            { showInvoice() }
            </IonCol>
          </IonRow>
          
          <AlertModal
            isOpen={isOpenModal}
            title={modalTitle}
            subTitle={modalMessage}
            onClosed={() => {
              setIsOpenModal(false);
              modal.current?.dismiss();
            }}
          />
          
          <IonAlert
            isOpen={showDoNotPayAlert}
            onDidDismiss={() => setShowDoNotPayAlert(false)}
            header="Do not pay"
            message="Please confirm that you do not want Billsmoov to pay this bill. Selecting this option means you need to pay this bill yourself."
            buttons={[
              {
                text: 'Confirm',
                handler: historical
              },
              {
                text: 'Cancel',
                role: 'cancel'
              }
            ]}
          />
        </IonGrid>
      </IonModal>
  );
}

export default InvoiceApproval;
